import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Button,
  TextField, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl,
  InputLabel, Select, MenuItem, Alert,
  CircularProgress, Tooltip, Skeleton, Divider,
  Switch, LinearProgress, Paper
} from '@mui/material';
import {
  Search, Add, Edit, Visibility,
  Person, PhoneAndroid, AccountBalance,
  LocalDrink, Close, Save, Refresh,
  PeopleAlt, Tag, ToggleOn, ToggleOff,
  Upload, Download, TableChart, CheckCircle,
  Error as ErrorIcon, Warning
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useApp } from '../context/AppContext';
import api from '../api/authApi';
import * as XLSX from 'xlsx';

const MILK_TYPES = [
  { id: 1, name: 'Cow' },
  { id: 2, name: 'Buffalo' },
  { id: 3, name: 'Cow + Buffalo' },
];

const EMPTY_FORM = {
  farmerId: 0, farmerCode: '', firstName: '', lastName: '', nameEnglish: '',
  phoneNumber: '', mobileNumber: '', milkTypeId: '', bankBranch: '',
  bankName: '', accountNumber: '', ifscCode: '', isActive: true,
};

const EXCEL_COLUMNS = [
  'Farmer Code *', 'First Name *', 'Last Name *', 'Name (English)',
  'Phone Number', 'Mobile Number (SMS)', 'Milk Type * (Cow/Buffalo/Cow + Buffalo)',
  'Bank Branch', 'Bank Name', 'Account Number', 'IFSC Code',
];

// Columns for Mass Code Update template
const CODE_UPDATE_COLUMNS = ['Farmer ID', 'Current Code', 'Farmer Name', 'New Code *'];

function StatBox({ label, value, color, bg }) {
  return (
    <Box sx={{ px: 2.5, py: 1.2, borderRadius: 2, background: bg, border: `1.5px solid ${color}33`, textAlign: 'center', minWidth: 90 }}>
      <Typography variant='h6' sx={{ fontWeight: 800, color, lineHeight: 1 }}>{value ?? '\u2014'}</Typography>
      <Typography variant='caption' sx={{ color: '#666', fontWeight: 600 }}>{label}</Typography>
    </Box>
  );
}

function SectionHeader({ icon, label }) {
  const { theme } = useApp();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pb: 1, borderBottom: '2px solid #f0fdf4' }}>
      {React.cloneElement(icon, { sx: { color: theme.primary, fontSize: 20 } })}
      <Typography variant='subtitle2' sx={{ fontWeight: 800, color: theme.primary, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>{label}</Typography>
    </Box>
  );
}

export default function Farmers() {
  const { theme } = useApp();

  const [farmers, setFarmers] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selected, setSelected] = useState(null);

  // ── Mass Upload State ─────────────────────────────
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadRows, setUploadRows] = useState([]);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadSkipped, setUploadSkipped] = useState([]); // ✅ codes skipped
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const fileInputRef = useRef(null);

  // ── Mass Code Update State ────────────────────────
  const [codeUpdateOpen, setCodeUpdateOpen] = useState(false);
  const [codeUpdateFile, setCodeUpdateFile] = useState(null);
  const [codeUpdateRows, setCodeUpdateRows] = useState([]);
  const [codeUpdateErrors, setCodeUpdateErrors] = useState([]);
  const [codeUpdating, setCodeUpdating] = useState(false);
  const [codeUpdateProgress, setCodeUpdateProgress] = useState(0);
  const [codeUpdateResults, setCodeUpdateResults] = useState(null);
  const codeFileInputRef = useRef(null);

  const btnSx = {
    background: `linear-gradient(135deg,${theme.primary},${theme.hover})`,
    borderRadius: 2, fontWeight: 700,
    '&:hover': { background: `linear-gradient(135deg,${theme.dark},${theme.primary})` }
  };

  // ── Fetch ─────────────────────────────────────────
  const fetchFarmers = useCallback(async (searchTerm = '') => {
    setLoading(true);
    try {
      const [farmersRes, statsRes] = await Promise.all([
        api.get('/farmer', { params: { search: searchTerm || undefined } }),
        api.get('/farmer/stats')
      ]);
      if (farmersRes.data.success) setFarmers(farmersRes.data.data || []);
      if (statsRes.data.success)   setStats(statsRes.data.data);
    } catch { setError('Failed to load farmers. Please check API connection.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchFarmers(); }, [fetchFarmers]);
  useEffect(() => {
    const t = setTimeout(() => fetchFarmers(search), 400);
    return () => clearTimeout(t);
  }, [search, fetchFarmers]);

  // ── Download Mass Upload Template ─────────────────
  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const sampleData = [
      EXCEL_COLUMNS,
      ['F001','Rajesh','Patil','Rajesh Patil','9876543210','9876543210','Cow','SBI Pune','State Bank of India','12345678901','SBIN0001234'],
      ['F002','Suman','Desai','Suman Desai','9123456789','9123456789','Buffalo','','','',''],
      ['F003','Meena','More','','8765432109','8765432109','Cow + Buffalo','','Bank of Maharashtra','98765432109','MAHB0001234'],
    ];
    const ws = XLSX.utils.aoa_to_sheet(sampleData);
    ws['!cols'] = [{wch:12},{wch:15},{wch:15},{wch:20},{wch:15},{wch:20},{wch:30},{wch:18},{wch:22},{wch:18},{wch:15}];
    const instructions = [
      ['DairyPro — Farmer Bulk Upload Instructions'],[''],
      ['REQUIRED FIELDS (marked with *):'],
      ['  • Farmer Code * — Must be unique. If code already exists, row is SKIPPED.'],
      ['  • First Name *  — Farmers first name'],
      ['  • Last Name *   — Farmers last name'],
      ['  • Milk Type *   — Must be: Cow, Buffalo, or Cow + Buffalo'],[''],
      ['NOTE: Duplicate codes within file or codes existing in system are skipped.'],
      ['Use Mass Code Update to change existing farmer codes.'],
    ];
    const wsInstr = XLSX.utils.aoa_to_sheet(instructions);
    wsInstr['!cols'] = [{wch:70}];
    XLSX.utils.book_append_sheet(wb, ws, 'Farmers Upload');
    XLSX.utils.book_append_sheet(wb, wsInstr, 'Instructions');
    XLSX.writeFile(wb, 'DairyPro_Farmers_Template.xlsx');
  };

  // ── Download Code Update Template (pre-filled with all farmers) ──
  const downloadCodeUpdateTemplate = async () => {
    try {
      const res = await api.get('/farmer', { params: { pageSize: 500 } });
      const allFarmers = res.data?.data || [];
      const wb = XLSX.utils.book_new();
      const rows = [CODE_UPDATE_COLUMNS, ...allFarmers.map(f => [f.farmerId, f.farmerCode || '', f.farmerName || '', ''])];
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [{wch:12},{wch:15},{wch:25},{wch:15}];
      const instructions = [
        ['DairyPro — Mass Code Update Instructions'],[''],
        ['  • Fill "New Code *" column (column D) ONLY'],
        ['  • DO NOT modify Farmer ID, Current Code, or Farmer Name columns'],
        ['  • Leave New Code blank to skip that farmer (no change)'],
        ['  • You can update any code, including changing existing codes to new values'],
        ['  • ONLY skipped if: same new code appears twice in this file (duplicate)'],
        ['  • ONLY skipped if: new code is already assigned to a DIFFERENT farmer'],
      ];
      const wsInstr = XLSX.utils.aoa_to_sheet(instructions);
      wsInstr['!cols'] = [{wch:70}];
      XLSX.utils.book_append_sheet(wb, ws, 'Code Update');
      XLSX.utils.book_append_sheet(wb, wsInstr, 'Instructions');
      XLSX.writeFile(wb, 'DairyPro_FarmerCode_Update.xlsx');
    } catch { setError('Failed to download template.'); }
  };

  // ── Parse uploaded Excel ──────────────────────────
  // ✅ Made async to fetch existing codes before parsing
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadFile(file);
    setUploadErrors([]); setUploadRows([]); setUploadSkipped([]); setUploadResults(null);

    // ✅ Fetch all existing farmer codes to detect duplicates
    let existingCodes = new Set();
    try {
      const res = await api.get('/farmer', { params: { pageSize: 500 } });
      if (res.data?.success) (res.data.data || []).forEach(f => {
        if (f.farmerCode) existingCodes.add(String(f.farmerCode).trim().toLowerCase());
      });
    } catch { /* continue without duplicate check */ }

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (raw.length < 2) { setUploadErrors(['File is empty or has no data rows.']); return; }
        const header = raw[0].map(h => String(h).trim());
        const missingCols = EXCEL_COLUMNS.filter(col => !header.includes(col));
        if (missingCols.length > 0) {
          setUploadErrors([`Header mismatch. Missing columns: ${missingCols.join(', ')}`, 'Please download the sample template and use it.']);
          return;
        }
        const colIdx = {};
        EXCEL_COLUMNS.forEach(col => { colIdx[col] = header.indexOf(col); });
        const rows = [], errors = [], skipped = [];
        const seenCodesInFile = new Map(); // track codes seen in this file
        raw.slice(1).forEach((row, i) => {
          const rowNum = i + 2;
          if (row.every(cell => String(cell).trim() === '')) return;
          const farmerCode = String(row[colIdx['Farmer Code *']] || '').trim();
          const firstName  = String(row[colIdx['First Name *']] || '').trim();
          const lastName   = String(row[colIdx['Last Name *']] || '').trim();
          const nameEng    = String(row[colIdx['Name (English)']] || '').trim();
          const phone      = String(row[colIdx['Phone Number']] || '').trim();
          const mobile     = String(row[colIdx['Mobile Number (SMS)']] || '').trim();
          const milkStr    = String(row[colIdx['Milk Type * (Cow/Buffalo/Cow + Buffalo)']] || '').trim();
          const bankBranch = String(row[colIdx['Bank Branch']] || '').trim();
          const bankName   = String(row[colIdx['Bank Name']] || '').trim();
          const accountNo  = String(row[colIdx['Account Number']] || '').trim();
          const ifsc       = String(row[colIdx['IFSC Code']] || '').trim().toUpperCase();

          // ✅ Skip if farmer code already exists in system
          if (farmerCode && existingCodes.has(farmerCode.toLowerCase())) {
            skipped.push(`Row ${rowNum}: Code "${farmerCode}" already exists in system — skipped`);
            return;
          }
          // ✅ Skip if duplicate code within this file
          if (farmerCode && seenCodesInFile.has(farmerCode.toLowerCase())) {
            skipped.push(`Row ${rowNum}: Code "${farmerCode}" is duplicate in file (row ${seenCodesInFile.get(farmerCode.toLowerCase())}) — skipped`);
            return;
          }
          if (farmerCode) seenCodesInFile.set(farmerCode.toLowerCase(), rowNum);

          const rowErrors = [];
          if (!farmerCode) rowErrors.push(`Row ${rowNum}: Farmer Code is required`);
          if (!firstName)  rowErrors.push(`Row ${rowNum}: First Name is required`);
          if (!lastName)   rowErrors.push(`Row ${rowNum}: Last Name is required`);
          const milkType = MILK_TYPES.find(m => m.name.toLowerCase() === milkStr.toLowerCase());
          if (!milkStr)       rowErrors.push(`Row ${rowNum}: Milk Type is required`);
          else if (!milkType) rowErrors.push(`Row ${rowNum}: Invalid Milk Type "${milkStr}" — use: Cow, Buffalo, or Cow + Buffalo`);
          if (phone  && !/^\d{10}$/.test(phone))  rowErrors.push(`Row ${rowNum}: Phone must be 10 digits`);
          if (mobile && !/^\d{10}$/.test(mobile)) rowErrors.push(`Row ${rowNum}: Mobile must be 10 digits`);
          if (ifsc   && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) rowErrors.push(`Row ${rowNum}: Invalid IFSC format`);

          if (rowErrors.length > 0) { errors.push(...rowErrors); }
          else {
            rows.push({ rowNum, farmerCode, firstName, lastName,
              nameEnglish: nameEng || null, phoneNumber: phone || null, mobileNumber: mobile || null,
              milkTypeId: milkType?.id, milkTypeName: milkStr,
              bankBranch: bankBranch || null, bankName: bankName || null,
              accountNumber: accountNo || null, ifscCode: ifsc || null, isActive: true });
          }
        });
        setUploadErrors(errors); setUploadSkipped(skipped); setUploadRows(rows);
      } catch (err) { setUploadErrors(['Failed to parse file: ' + err.message]); }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  // ── Upload farmers to API ─────────────────────────
  const handleBulkUpload = async () => {
    if (uploadRows.length === 0) return;
    setUploading(true); setUploadProgress(0);
    const results = { success: 0, failed: 0, errors: [] };
    const batchSize = 10;
    for (let i = 0; i < uploadRows.length; i += batchSize) {
      const batch = uploadRows.slice(i, i + batchSize);
      await Promise.all(batch.map(async (row) => {
        try {
          const res = await api.post('/farmer', {
            farmerCode: row.farmerCode, firstName: row.firstName, lastName: row.lastName,
            nameEnglish: row.nameEnglish, phoneNumber: row.phoneNumber, mobileNumber: row.mobileNumber,
            milkTypeId: row.milkTypeId, bankBranch: row.bankBranch, bankName: row.bankName,
            accountNumber: row.accountNumber, ifscCode: row.ifscCode, isActive: true,
          });
          if (res.data?.success) results.success++;
          else { results.failed++; results.errors.push(`Row ${row.rowNum} (${row.farmerCode}): ${res.data?.message || 'Failed'}`); }
        } catch (e) { results.failed++; results.errors.push(`Row ${row.rowNum} (${row.farmerCode}): ${e.message}`); }
      }));
      setUploadProgress(Math.round(((i + batchSize) / uploadRows.length) * 100));
    }
    setUploading(false); setUploadProgress(100); setUploadResults(results); fetchFarmers(search);
  };

  const resetUpload = () => {
    setUploadFile(null); setUploadRows([]); setUploadErrors([]);
    setUploadSkipped([]); setUploadResults(null); setUploadProgress(0); setUploading(false);
  };

  // ── Parse Code Update Excel ───────────────────────
  const handleCodeUpdateFileChange = (e) => {
    const file = e.target.files[0]; // ✅ save ref immediately
    if (!file) return;
    // ✅ Reset input AFTER saving file ref, not before
    const input = e.target;
    setCodeUpdateFile(file); setCodeUpdateErrors([]); setCodeUpdateRows([]); setCodeUpdateResults(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (raw.length < 2) { setCodeUpdateErrors(['File is empty or has no data rows.']); return; }

        // ✅ Flexible header matching — trim + case-insensitive
        const header = raw[0].map(h => String(h).trim());

        // Find columns flexibly
        const findCol = (names) => {
          for (const name of names) {
            const idx = header.findIndex(h => h.toLowerCase().replace(/[^a-z0-9]/g,'') === name.toLowerCase().replace(/[^a-z0-9]/g,''));
            if (idx !== -1) return idx;
          }
          return -1;
        };

        const idIdx      = findCol(['Farmer ID', 'FarmerID', 'ID', 'farmerId']);
        const newCodeIdx = findCol(['New Code *', 'New Code', 'NewCode', 'newcode']);
        const nameIdx    = findCol(['Farmer Name', 'FarmerName', 'Name']);
        const curCodeIdx = findCol(['Current Code', 'CurrentCode', 'Code']);

        if (idIdx === -1 || newCodeIdx === -1) {
          setCodeUpdateErrors([
            `Could not find required columns in file.`,
            `Found: ${header.join(', ')}`,
            `Expected: "Farmer ID" and "New Code *" columns.`,
            'Please download a fresh template and try again.'
          ]);
          return;
        }

        const rows = [], errors = [];
        const seenNewCodes = new Map();

        raw.slice(1).forEach((row, i) => {
          const rowNum = i + 2;
          if (row.every(cell => String(cell).trim() === '')) return;
          const farmerId   = parseInt(String(row[idIdx] || '').trim());
          const newCode    = String(newCodeIdx !== -1 ? (row[newCodeIdx] || '') : '').trim();
          const farmerName = String(nameIdx !== -1 ? (row[nameIdx] || '') : '').trim();
          const curCode    = String(curCodeIdx !== -1 ? (row[curCodeIdx] || '') : '').trim();

          if (!newCode) return; // blank = skip this farmer, no error
          if (!farmerId || isNaN(farmerId)) { errors.push(`Row ${rowNum}: Invalid or missing Farmer ID`); return; }

          // ✅ Duplicate new code within file
          if (seenNewCodes.has(newCode.toLowerCase())) {
            errors.push(`Row ${rowNum} (${farmerName}): Code "${newCode}" is duplicate in file (row ${seenNewCodes.get(newCode.toLowerCase())}) — skipped`);
            return;
          }
          seenNewCodes.set(newCode.toLowerCase(), rowNum);
          rows.push({ rowNum, farmerId, farmerName, currentCode: curCode, newCode });
        });

        setCodeUpdateErrors(errors); setCodeUpdateRows(rows);
      } catch (err) { setCodeUpdateErrors(['Failed to parse file: ' + err.message]); }
    };
    reader.readAsBinaryString(file);
    // ✅ Reset after FileReader starts (not before)
    setTimeout(() => { try { input.value = ''; } catch(ex) {} }, 100);
  };

  // ── Execute Mass Code Update ──────────────────────
  const handleMassCodeUpdate = async () => {
    if (codeUpdateRows.length === 0) return;
    setCodeUpdating(true); setCodeUpdateProgress(0);
    const results = { success: 0, failed: 0, errors: [] };
    let allFarmersMap = {}, existingCodeMap = {};
    try {
      const res = await api.get('/farmer', { params: { pageSize: 500 } });
      (res.data?.data || []).forEach(f => {
        allFarmersMap[f.farmerId] = f;
        if (f.farmerCode) existingCodeMap[f.farmerCode.toLowerCase()] = f.farmerId;
      });
    } catch {
      setCodeUpdateResults({ success: 0, failed: codeUpdateRows.length, errors: ['Failed to fetch farmer data'] });
      setCodeUpdating(false); return;
    }
    // ✅ Pre-filter: skip rows where new code is already used by a DIFFERENT farmer
    const validRows = [];
    for (const row of codeUpdateRows) {
      const existingOwner = existingCodeMap[row.newCode.toLowerCase()];
      if (existingOwner && existingOwner !== row.farmerId) {
        results.failed++;
        results.errors.push(`Row ${row.rowNum} (${row.farmerName}): Code "${row.newCode}" already used by another farmer — skipped`);
      } else { validRows.push(row); }
    }
    const batchSize = 5;
    for (let i = 0; i < validRows.length; i += batchSize) {
      const batch = validRows.slice(i, i + batchSize);
      await Promise.all(batch.map(async (row) => {
        const farmer = allFarmersMap[row.farmerId];
        if (!farmer) { results.failed++; results.errors.push(`Row ${row.rowNum}: Farmer ID ${row.farmerId} not found`); return; }
        try {
          const res = await api.post('/farmer/update', {
            farmerId: farmer.farmerId, farmerCode: row.newCode,
            firstName: farmer.firstName, lastName: farmer.lastName,
            nameEnglish: farmer.nameEnglish || null, phoneNumber: farmer.phoneNumber || null,
            mobileNumber: farmer.mobileNumber || null, milkTypeId: farmer.milkTypeId,
            bankBranch: farmer.bankBranch || null, bankName: farmer.bankName || null,
            accountNumber: farmer.accountNumber || null, ifscCode: farmer.ifscCode || null,
            isActive: farmer.isActive === 1 || farmer.isActive === true,
          });
          if (res.data?.success) results.success++;
          else { results.failed++; results.errors.push(`Row ${row.rowNum} (${row.farmerName}): ${res.data?.message || 'Failed'}`); }
        } catch (e) { results.failed++; results.errors.push(`Row ${row.rowNum} (${row.farmerName}): ${e.message}`); }
      }));
      setCodeUpdateProgress(Math.round(((i + batchSize) / validRows.length) * 100));
    }
    setCodeUpdating(false); setCodeUpdateProgress(100); setCodeUpdateResults(results); fetchFarmers(search);
  };

  const resetCodeUpdate = () => {
    setCodeUpdateFile(null); setCodeUpdateRows([]); setCodeUpdateErrors([]);
    setCodeUpdateResults(null); setCodeUpdateProgress(0); setCodeUpdating(false);
  };

  // ── Add / Edit / View / Status ────────────────────
  const handleAdd = () => { setForm(EMPTY_FORM); setIsEdit(false); setError(''); setDialogOpen(true); };
  const handleEdit = (farmer) => {
    setForm({
      farmerId: farmer.farmerId, farmerCode: farmer.farmerCode || '',
      firstName: farmer.firstName || '', lastName: farmer.lastName || '',
      nameEnglish: farmer.nameEnglish || '', phoneNumber: farmer.phoneNumber || '',
      mobileNumber: farmer.mobileNumber || '', milkTypeId: farmer.milkTypeId || '',
      bankBranch: farmer.bankBranch || '', bankName: farmer.bankName || '',
      accountNumber: farmer.accountNumber || '', ifscCode: farmer.ifscCode || '',
      isActive: farmer.isActive === 1 || farmer.isActive === true,
    });
    setIsEdit(true); setError(''); setDialogOpen(true);
  };
  const handleView          = (farmer) => { setSelected(farmer); setViewOpen(true); };
  const handleStatusConfirm = (farmer) => { setSelected(farmer); setStatusOpen(true); };
  const handleToggleStatus  = async () => {
    if (!selected) return;
    const id = selected.farmerId; setTogglingId(id); setStatusOpen(false);
    try {
      const res = await api.post(`/farmer/${id}/toggle-active`);
      if (res.data.success) {
        setSuccess(res.data.message || 'Status updated.');
        setFarmers(prev => prev.map(f => f.farmerId === id ? { ...f, isActive: f.isActive === 1 ? 0 : 1 } : f));
        api.get('/farmer/stats').then(r => { if (r.data.success) setStats(r.data.data); });
        setTimeout(() => setSuccess(''), 3000);
      } else { setError(res.data.message || 'Failed to update status.'); }
    } catch { setError('Cannot connect to server.'); }
    finally { setTogglingId(null); setSelected(null); }
  };
  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const handleReset  = () => { setForm(prev => ({ ...EMPTY_FORM, farmerId: prev.farmerId, farmerCode: prev.farmerCode })); setError(''); };
  const handleSave   = async () => {
    setError('');
    if (!form.firstName.trim()) { setError('Please enter first name.'); return; }
    if (!form.lastName.trim())  { setError('Please enter last name.');  return; }
    if (!form.milkTypeId)       { setError('Please select milk type.'); return; }
    // ✅ Validate farmer code uniqueness if editing and code changed
    if (isEdit && form.farmerCode) {
      try {
        const existing = farmers.find(f =>
          f.farmerId !== form.farmerId &&
          f.farmerCode &&
          f.farmerCode.toLowerCase() === form.farmerCode.toLowerCase()
        );
        if (existing) {
          setError(`Farmer code "${form.farmerCode}" is already used by ${existing.farmerName}. Please use a different code.`);
          return;
        }
      } catch { /* continue */ }
    }
    setSaving(true);
    try {
      const payload = {
        farmerCode: form.farmerCode || '', firstName: form.firstName.trim(), lastName: form.lastName.trim(),
        nameEnglish: form.nameEnglish || null, phoneNumber: form.phoneNumber || null,
        mobileNumber: form.mobileNumber || null, milkTypeId: parseInt(form.milkTypeId),
        bankBranch: form.bankBranch || null, bankName: form.bankName || null,
        accountNumber: form.accountNumber || null, ifscCode: form.ifscCode || null, isActive: form.isActive,
      };
      const res = isEdit
        ? await api.post('/farmer/update', { ...payload, farmerId: form.farmerId })
        : await api.post('/farmer', payload);
      if (res.data.success) {
        setSuccess(isEdit ? 'Farmer updated!' : 'Farmer added!');
        setDialogOpen(false); fetchFarmers(search); setTimeout(() => setSuccess(''), 3000);
      } else {
        // ✅ Show API error — may include duplicate code message from server
        setError(res.data.message || 'Something went wrong.');
      }
    } catch { setError('Cannot connect to server. Please try again.'); }
    finally { setSaving(false); }
  };
  const getMilkTypeName = (id) => MILK_TYPES.find(m => m.id === id)?.name || '—';
  const isActive = (f) => f?.isActive === 1 || f?.isActive === true;

  return (
    <Layout title="Farmers">
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error   && <Alert severity="error"   sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
        <CardContent sx={{ p: 0 }}>

          {/* Header */}
          <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0', flexWrap: 'wrap', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleAlt sx={{ color: theme.primary, fontSize: 26 }} />
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Farmers</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {/* ✅ Mass Code Update button */}
              <Button variant="outlined" startIcon={<Edit />}
                onClick={() => { resetCodeUpdate(); setCodeUpdateOpen(true); }}
                sx={{ borderRadius: 2, borderColor: '#f97316', color: '#f97316', fontWeight: 700, '&:hover': { background: '#fff7ed' } }}>
                Mass Code Update
              </Button>
              <Button variant="outlined" startIcon={<Upload />}
                onClick={() => { resetUpload(); setUploadOpen(true); }}
                sx={{ borderRadius: 2, borderColor: theme.primary, color: theme.primary, fontWeight: 700, '&:hover': { background: theme.light } }}>
                Mass Upload
              </Button>
              <Button variant="contained" startIcon={<Add />} onClick={handleAdd} sx={btnSx}>Add Farmer</Button>
            </Box>
          </Box>

          {/* Stats + Search */}
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, background: '#fafffe', borderBottom: '1px solid #f0f0f0' }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <StatBox label="Active"   value={stats?.activeFarmers}   color={theme.primary} bg={theme.light} />
              <StatBox label="Inactive" value={stats?.inactiveFarmers} color="#e53e3e" bg="#fff5f5" />
              <StatBox label={`Total / ${stats?.maxAllowed ?? '—'}`} value={stats?.totalFarmers} color="#3b82f6" bg="#eff6ff" />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" placeholder="Search farmers..." value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: '#aaa', fontSize: 18 }} /></InputAdornment>, sx: { borderRadius: 2, fontSize: '0.875rem' } }}
                sx={{ width: 220 }} />
              <Tooltip title="Refresh">
                <IconButton onClick={() => fetchFarmers(search)} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, color: theme.primary }}><Refresh fontSize="small" /></IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: '#f8fafc' }}>
                  {['Farmer Code','Farmer Name','Name (English)','Phone Number','Status','Actions'].map(col => (
                    <TableCell key={col} sx={{ fontWeight: 700, color: '#555', fontSize: '0.8rem', py: 1.2, borderBottom: '2px solid #f0f0f0' }}>{col}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>{Array.from({ length: 6 }).map((_, j) => (
                        <TableCell key={j}><Skeleton variant="text" width="80%" /></TableCell>
                      ))}</TableRow>
                    ))
                  : farmers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 7 }}>
                        <Person sx={{ fontSize: 52, color: '#e0e0e0', mb: 1 }} />
                        <Typography variant="body2" sx={{ color: '#bbb' }}>{search ? `No farmers found for "${search}"` : 'No farmers yet.'}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : farmers.map((farmer, idx) => (
                    <TableRow key={farmer.farmerId} sx={{ background: idx % 2 === 0 ? '#fff' : '#fafffe', '&:hover': { background: theme.light }, opacity: isActive(farmer) ? 1 : 0.65 }}>
                      <TableCell sx={{ fontWeight: 700, color: theme.primary, py: 1.2 }}>{farmer.farmerCode || '—'}</TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 1.2 }}>{farmer.farmerName}</TableCell>
                      <TableCell sx={{ color: '#888', py: 1.2 }}>{farmer.nameEnglish || '—'}</TableCell>
                      <TableCell sx={{ color: '#555', py: 1.2 }}>{farmer.phoneNumber || '—'}</TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        <Chip label={isActive(farmer) ? 'Active' : 'Inactive'} size="small"
                          sx={{ fontWeight: 700, fontSize: '0.7rem', background: isActive(farmer) ? theme.light : '#fff5f5', color: isActive(farmer) ? theme.primary : '#e53e3e', border: `1px solid ${isActive(farmer) ? '#1a7a4a33' : '#e53e3e33'}` }} />
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Tooltip title="View Details"><IconButton size="small" onClick={() => handleView(farmer)} sx={{ color: '#3b82f6' }}><Visibility fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Edit Farmer"><IconButton size="small" onClick={() => handleEdit(farmer)} sx={{ color: theme.primary }}><Edit fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title={isActive(farmer) ? 'Deactivate' : 'Activate'}>
                            <IconButton size="small" onClick={() => handleStatusConfirm(farmer)} disabled={togglingId === farmer.farmerId} sx={{ color: isActive(farmer) ? '#f97316' : theme.primary }}>
                              {togglingId === farmer.farmerId ? <CircularProgress size={16} /> : isActive(farmer) ? <ToggleOn fontSize="small" /> : <ToggleOff fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && farmers.length > 0 && (
            <Box sx={{ px: 2.5, py: 1.2, borderTop: '1px solid #f0f0f0', background: '#fafafa' }}>
              <Typography variant="caption" sx={{ color: '#aaa' }}>
                Showing {farmers.length} farmer{farmers.length !== 1 ? 's' : ''}{search && ` matching "${search}"`}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ══════════════════════════════════════════
          MASS UPLOAD DIALOG
      ══════════════════════════════════════════ */}
      <Dialog open={uploadOpen} onClose={() => !uploading && setUploadOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: `linear-gradient(135deg,${theme.primary},${theme.hover})`, color: '#fff', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Upload /><Typography variant="h6" sx={{ fontWeight: 700 }}>Mass Upload Farmers</Typography></Box>
          {!uploading && <IconButton onClick={() => setUploadOpen(false)} sx={{ color: '#fff' }}><Close /></IconButton>}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {/* Step 1 */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2, borderColor: '#e0f2e9' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: theme.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>1</Typography></Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.primary }}>Download Sample Excel Template</Typography>
            </Box>
            <Alert severity="info" sx={{ mb: 1.5, borderRadius: 1.5, py: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                ℹ️ Farmer Code is read from Excel. If a code <strong>already exists</strong> in the system, that row is <strong>skipped automatically</strong>. Duplicate codes within the file are also skipped.
              </Typography>
            </Alert>
            <Button variant="contained" startIcon={<Download />} onClick={downloadTemplate} sx={{ background: '#059669', borderRadius: 2, fontWeight: 700, '&:hover': { background: '#047857' } }}>
              Download Excel Template
            </Button>
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
              {EXCEL_COLUMNS.map(col => (
                <Chip key={col} label={col} size="small" sx={{ fontSize: '0.68rem', fontWeight: 600, background: col.includes('*') ? '#ECFDF5' : '#F9FAFB', color: col.includes('*') ? theme.primary : '#666', border: `1px solid ${col.includes('*') ? '#86EFAC' : '#E5E7EB'}` }} />
              ))}
            </Box>
            <Typography variant="caption" sx={{ color: '#059669', display: 'block', mt: 1 }}>✅ Green = Required fields</Typography>
          </Paper>
          {/* Step 2 */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: theme.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>2</Typography></Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.primary }}>Upload Filled Excel File</Typography>
            </Box>
            <Box onClick={() => !uploading && fileInputRef.current?.click()}
              sx={{ border: `2px dashed ${uploadFile ? theme.primary : '#D1D5DB'}`, borderRadius: 2, p: 4, textAlign: 'center', cursor: uploading ? 'not-allowed' : 'pointer', background: uploadFile ? '#ECFDF5' : '#FAFAFA', transition: 'all 0.2s', '&:hover': { borderColor: theme.primary, background: '#ECFDF5' } }}>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFileChange} />
              <TableChart sx={{ fontSize: 40, color: uploadFile ? theme.primary : '#9CA3AF', mb: 1 }} />
              {uploadFile ? (
                <><Typography variant="body2" sx={{ fontWeight: 700, color: theme.primary }}>✅ {uploadFile.name}</Typography><Typography variant="caption" sx={{ color: '#059669' }}>Click to change file</Typography></>
              ) : (
                <><Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>Click to select Excel file</Typography><Typography variant="caption" sx={{ color: '#9CA3AF' }}>.xlsx or .xls — max 500 farmers</Typography></>
              )}
            </Box>
          </Paper>
          {/* ✅ Skipped rows notice */}
          {uploadSkipped.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }} icon={<Warning />}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{uploadSkipped.length} row{uploadSkipped.length !== 1 ? 's' : ''} skipped (code already exists or duplicate in file)</Typography>
              <Box sx={{ maxHeight: 100, overflowY: 'auto' }}>
                {uploadSkipped.map((e, i) => <Typography key={i} variant="caption" sx={{ display: 'block', lineHeight: 1.8 }}>• {e}</Typography>)}
              </Box>
            </Alert>
          )}
          {uploadErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} icon={<ErrorIcon />}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{uploadErrors.length} validation error{uploadErrors.length !== 1 ? 's' : ''} found — fix in Excel and re-upload</Typography>
              <Box sx={{ maxHeight: 120, overflowY: 'auto' }}>
                {uploadErrors.map((e, i) => <Typography key={i} variant="caption" sx={{ display: 'block', lineHeight: 1.8 }}>• {e}</Typography>)}
              </Box>
            </Alert>
          )}
          {uploadRows.length > 0 && !uploadResults && (
            <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
              <Box sx={{ px: 2, py: 1.2, background: '#F8FFFE', borderBottom: '1px solid #E0F2E9', display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle sx={{ color: theme.primary, fontSize: 18 }} />
                <Typography variant="body2" sx={{ fontWeight: 700, color: theme.primary }}>
                  {uploadRows.length} farmers ready to upload{uploadSkipped.length > 0 && ` • ${uploadSkipped.length} skipped`}
                </Typography>
              </Box>
              <Box sx={{ overflowX: 'auto', maxHeight: 260 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: '#F1FAF4' }}>
                      {['Row','Code','First Name','Last Name','Mobile','Milk Type'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.8 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uploadRows.slice(0, 20).map((row, i) => (
                      <TableRow key={i} sx={{ background: i % 2 === 0 ? '#fff' : '#FAFFFE' }}>
                        <TableCell sx={{ fontSize: '0.7rem', color: '#aaa', py: 0.6 }}>{row.rowNum}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: theme.primary, py: 0.6 }}>{row.farmerCode}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, py: 0.6 }}>{row.firstName}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', py: 0.6 }}>{row.lastName}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: '#555', py: 0.6 }}>{row.mobileNumber || '—'}</TableCell>
                        <TableCell sx={{ py: 0.6 }}><Chip label={row.milkTypeName} size="small" sx={{ fontSize: '0.65rem', height: 18, fontWeight: 600, background: '#ECFDF5', color: theme.primary }} /></TableCell>
                      </TableRow>
                    ))}
                    {uploadRows.length > 20 && <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 1, color: '#888', fontSize: '0.75rem', fontStyle: 'italic' }}>... and {uploadRows.length - 20} more rows</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          )}
          {uploading && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.primary }}>Uploading farmers...</Typography>
                <Typography variant="body2" sx={{ color: '#555' }}>{uploadProgress}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={uploadProgress} sx={{ borderRadius: 4, height: 8, '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg,${theme.primary},#059669)` } }} />
            </Box>
          )}
          {uploadResults && (
            <Box>
              <Alert severity={uploadResults.failed === 0 ? 'success' : 'warning'} sx={{ mb: 1.5, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Upload complete! ✅ {uploadResults.success} added{uploadResults.failed > 0 && ` — ❌ ${uploadResults.failed} failed`}{uploadSkipped.length > 0 && ` — ⚠️ ${uploadSkipped.length} skipped`}
                </Typography>
              </Alert>
              {uploadResults.errors.length > 0 && (
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, background: '#FEF2F2', maxHeight: 120, overflowY: 'auto' }}>
                  {uploadResults.errors.map((e, i) => <Typography key={i} variant="caption" sx={{ display: 'block', color: '#DC2626', lineHeight: 1.8 }}>• {e}</Typography>)}
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button onClick={resetUpload} variant="outlined" disabled={uploading} sx={{ borderRadius: 2, color: '#888', borderColor: '#ddd' }}>Reset</Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setUploadOpen(false)} disabled={uploading} variant="outlined" sx={{ borderRadius: 2 }}>{uploadResults ? 'Close' : 'Cancel'}</Button>
            {!uploadResults && (
              <Button onClick={handleBulkUpload} variant="contained" disabled={uploadRows.length === 0 || uploading || uploadErrors.length > 0}
                startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <Upload />} sx={btnSx}>
                {uploading ? `Uploading... ${uploadProgress}%` : `Upload ${uploadRows.length} Farmers`}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════════
          MASS CODE UPDATE DIALOG
      ══════════════════════════════════════════ */}
      <Dialog open={codeUpdateOpen} onClose={() => !codeUpdating && setCodeUpdateOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg,#f97316,#ea580c)', color: '#fff', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Edit /><Typography variant="h6" sx={{ fontWeight: 700 }}>Mass Code Update</Typography></Box>
          {!codeUpdating && <IconButton onClick={() => setCodeUpdateOpen(false)} sx={{ color: '#fff' }}><Close /></IconButton>}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {/* Step 1 */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2, borderColor: '#fed7aa' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: '#f97316', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>1</Typography></Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f97316' }}>Download Code Update Template</Typography>
            </Box>
            <Alert severity="info" sx={{ mb: 1.5, borderRadius: 1.5, py: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Template is pre-filled with all your farmers. Fill <strong>"New Code *"</strong> column only.<br/>
                ✅ You can update <strong>any farmer's code</strong>, including existing ones.<br/>
                ❌ Only skipped if: same new code given to <strong>two different farmers</strong> in the file, or new code already belongs to a <strong>different farmer</strong>.
              </Typography>
            </Alert>
            <Button variant="contained" startIcon={<Download />} onClick={downloadCodeUpdateTemplate} sx={{ background: '#f97316', borderRadius: 2, fontWeight: 700, '&:hover': { background: '#ea580c' } }}>
              Download Code Update Template
            </Button>
          </Paper>
          {/* Step 2 */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: '#f97316', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>2</Typography></Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f97316' }}>Upload Filled Template</Typography>
            </Box>
            <Box onClick={() => !codeUpdating && codeFileInputRef.current?.click()}
              sx={{ border: `2px dashed ${codeUpdateFile ? '#f97316' : '#D1D5DB'}`, borderRadius: 2, p: 4, textAlign: 'center', cursor: codeUpdating ? 'not-allowed' : 'pointer', background: codeUpdateFile ? '#fff7ed' : '#FAFAFA', transition: 'all 0.2s', '&:hover': { borderColor: '#f97316', background: '#fff7ed' } }}>
              <input ref={codeFileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleCodeUpdateFileChange} />
              <TableChart sx={{ fontSize: 40, color: codeUpdateFile ? '#f97316' : '#9CA3AF', mb: 1 }} />
              {codeUpdateFile ? (
                <><Typography variant="body2" sx={{ fontWeight: 700, color: '#f97316' }}>✅ {codeUpdateFile.name}</Typography><Typography variant="caption" sx={{ color: '#ea580c' }}>Click to change file</Typography></>
              ) : (
                <><Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>Click to select filled template</Typography><Typography variant="caption" sx={{ color: '#9CA3AF' }}>.xlsx or .xls files only</Typography></>
              )}
            </Box>
          </Paper>
          {codeUpdateErrors.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{codeUpdateErrors.length} issue{codeUpdateErrors.length !== 1 ? 's' : ''} found</Typography>
              <Box sx={{ maxHeight: 120, overflowY: 'auto' }}>
                {codeUpdateErrors.map((e, i) => <Typography key={i} variant="caption" sx={{ display: 'block', lineHeight: 1.8 }}>• {e}</Typography>)}
              </Box>
            </Alert>
          )}
          {codeUpdateRows.length > 0 && !codeUpdateResults && (
            <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
              <Box sx={{ px: 2, py: 1.2, background: '#fff7ed', borderBottom: '1px solid #fed7aa', display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle sx={{ color: '#f97316', fontSize: 18 }} />
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#f97316' }}>{codeUpdateRows.length} farmers ready for code update</Typography>
              </Box>
              <Box sx={{ overflowX: 'auto', maxHeight: 260 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: '#fff7ed' }}>
                      {['Row','ID','Farmer Name','Current Code','New Code'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.8 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {codeUpdateRows.slice(0, 20).map((row, i) => (
                      <TableRow key={i} sx={{ background: i % 2 === 0 ? '#fff' : '#fffbf7' }}>
                        <TableCell sx={{ fontSize: '0.7rem', color: '#aaa', py: 0.6 }}>{row.rowNum}</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem', color: '#888', py: 0.6 }}>{row.farmerId}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, py: 0.6 }}>{row.farmerName}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.6 }}><Typography component="span" sx={{ textDecoration: 'line-through', color: '#aaa', fontSize: '0.75rem' }}>{row.currentCode || '—'}</Typography></TableCell>
                        <TableCell sx={{ py: 0.6 }}><Chip label={row.newCode} size="small" sx={{ fontSize: '0.68rem', height: 20, fontWeight: 700, background: '#fff7ed', color: '#f97316', border: '1px solid #fed7aa' }} /></TableCell>
                      </TableRow>
                    ))}
                    {codeUpdateRows.length > 20 && <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', py: 1, color: '#888', fontSize: '0.75rem', fontStyle: 'italic' }}>... and {codeUpdateRows.length - 20} more</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          )}
          {codeUpdating && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#f97316' }}>Updating codes...</Typography>
                <Typography variant="body2" sx={{ color: '#555' }}>{codeUpdateProgress}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={codeUpdateProgress} sx={{ borderRadius: 4, height: 8, '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#f97316,#ea580c)' } }} />
            </Box>
          )}
          {codeUpdateResults && (
            <Box>
              <Alert severity={codeUpdateResults.failed === 0 ? 'success' : 'warning'} sx={{ mb: 1.5, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  ✅ {codeUpdateResults.success} updated{codeUpdateResults.failed > 0 && ` — ❌ ${codeUpdateResults.failed} failed/skipped`}
                </Typography>
              </Alert>
              {codeUpdateResults.errors.length > 0 && (
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, background: '#FEF2F2', maxHeight: 120, overflowY: 'auto' }}>
                  {codeUpdateResults.errors.map((e, i) => <Typography key={i} variant="caption" sx={{ display: 'block', color: '#DC2626', lineHeight: 1.8 }}>• {e}</Typography>)}
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button onClick={resetCodeUpdate} variant="outlined" disabled={codeUpdating} sx={{ borderRadius: 2, color: '#888', borderColor: '#ddd' }}>Reset</Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setCodeUpdateOpen(false)} disabled={codeUpdating} variant="outlined" sx={{ borderRadius: 2 }}>{codeUpdateResults ? 'Close' : 'Cancel'}</Button>
            {!codeUpdateResults && (
              <Button onClick={handleMassCodeUpdate} variant="contained" disabled={codeUpdateRows.length === 0 || codeUpdating}
                startIcon={codeUpdating ? <CircularProgress size={16} color="inherit" /> : <Save />}
                sx={{ background: '#f97316', borderRadius: 2, fontWeight: 700, '&:hover': { background: '#ea580c' } }}>
                {codeUpdating ? `Updating... ${codeUpdateProgress}%` : `Update ${codeUpdateRows.length} Codes`}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* ── ADD / EDIT DIALOG ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: `linear-gradient(135deg,${theme.primary},${theme.hover})`, color: '#fff', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Person /><Typography variant="h6" sx={{ fontWeight: 700 }}>{isEdit ? 'Edit Farmer' : 'Add Farmer'}</Typography></Box>
          <IconButton onClick={() => setDialogOpen(false)} sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 2.5 }}>
          {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <SectionHeader icon={<Person />} label="Personal Details" />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
            {isEdit ? (
              <TextField label={<>Farmer Code <span style={{ color: '#e53e3e' }}>*</span></>}
                value={form.farmerCode}
                onChange={(e) => handleChange('farmerCode', e.target.value.trim())}
                fullWidth size="small"
                InputProps={{ startAdornment: <InputAdornment position="start"><Tag sx={{ fontSize: 16, color: theme.primary }} /></InputAdornment> }}
                helperText="Must be unique across all farmers" />
            ) : (
              <TextField label="Farmer Code" value="Auto Generated" disabled fullWidth size="small"
                InputProps={{ startAdornment: <InputAdornment position="start"><Tag sx={{ fontSize: 16, color: theme.primary }} /></InputAdornment> }}
                sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#aaa', fontWeight: 600 } }} />
            )}
            <Box />
            <TextField label={<>First Name <span style={{ color: '#e53e3e' }}>*</span></>} value={form.firstName} onChange={(e) => handleChange('firstName', e.target.value)} fullWidth size="small" />
            <TextField label={<>Last Name <span style={{ color: '#e53e3e' }}>*</span></>} value={form.lastName} onChange={(e) => handleChange('lastName', e.target.value)} fullWidth size="small" />
            <TextField label="Name (English)" value={form.nameEnglish} onChange={(e) => handleChange('nameEnglish', e.target.value)} fullWidth size="small" />
            <TextField label="Phone Number" value={form.phoneNumber} onChange={(e) => handleChange('phoneNumber', e.target.value.replace(/\D/g,'').slice(0,10))}
              inputProps={{ maxLength: 10, inputMode: 'numeric' }}
              InputProps={{ startAdornment: <InputAdornment position="start"><PhoneAndroid sx={{ fontSize: 16, color: '#aaa' }} /></InputAdornment> }} fullWidth size="small" />
            <TextField label="Mobile Number (SMS)" value={form.mobileNumber} onChange={(e) => handleChange('mobileNumber', e.target.value.replace(/\D/g,'').slice(0,10))}
              inputProps={{ maxLength: 10, inputMode: 'numeric' }}
              InputProps={{ startAdornment: <InputAdornment position="start"><PhoneAndroid sx={{ fontSize: 16, color: theme.primary }} /></InputAdornment> }}
              fullWidth size="small" helperText="Used for SMS notifications" />
          </Box>
          <SectionHeader icon={<LocalDrink />} label="Milk Details" />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Milk Type <span style={{ color: '#e53e3e' }}>*</span></InputLabel>
              <Select value={form.milkTypeId} label="Milk Type *" onChange={(e) => handleChange('milkTypeId', e.target.value)}>
                <MenuItem value=""><em>Select Milk Type</em></MenuItem>
                {MILK_TYPES.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
              </Select>
            </FormControl>
            {isEdit && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid #e0e0e0', borderRadius: 1, px: 1.5, py: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#555', flexGrow: 1 }}>Status</Typography>
                <Typography variant="caption" sx={{ color: form.isActive ? theme.primary : '#e53e3e', fontWeight: 700 }}>{form.isActive ? 'Active' : 'Inactive'}</Typography>
                <Switch checked={form.isActive} onChange={(e) => handleChange('isActive', e.target.checked)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: theme.primary }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: theme.primary } }} />
              </Box>
            )}
          </Box>
          <SectionHeader icon={<AccountBalance />} label="Bank Details" />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField label="Branch"         value={form.bankBranch}    onChange={(e) => handleChange('bankBranch',    e.target.value)} fullWidth size="small" />
            <TextField label="Bank Name"      value={form.bankName}      onChange={(e) => handleChange('bankName',      e.target.value)} fullWidth size="small" />
            <TextField label="Account Number" value={form.accountNumber} onChange={(e) => handleChange('accountNumber', e.target.value)} fullWidth size="small" />
            <TextField label="IFSC Code"      value={form.ifscCode}      onChange={(e) => handleChange('ifscCode',      e.target.value.toUpperCase())} fullWidth size="small" />
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleReset} variant="outlined" sx={{ borderRadius: 2, color: '#888', borderColor: '#ddd' }}>Reset</Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2, color: '#555', borderColor: '#ddd' }}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />} sx={btnSx}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ── VIEW DIALOG ── */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: `linear-gradient(135deg,${theme.primary},${theme.hover})`, color: '#fff', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Visibility /><Typography variant="h6" sx={{ fontWeight: 700 }}>Farmer Details</Typography></Box>
          <IconButton onClick={() => setViewOpen(false)} sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        {selected && (
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, p: 2, background: theme.light, borderRadius: 2, border: '1px solid #1a7a4a22' }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#888' }}>Farmer Code</Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: theme.primary, lineHeight: 1.2 }}>{selected.farmerCode || '—'}</Typography>
              </Box>
              <Chip label={isActive(selected) ? 'Active' : 'Inactive'} sx={{ fontWeight: 700, background: isActive(selected) ? theme.light : '#fff5f5', color: isActive(selected) ? theme.primary : '#e53e3e', border: `1px solid ${isActive(selected) ? '#1a7a4a55' : '#e53e3e55'}` }} />
            </Box>
            {[
              { label: 'Full Name',      value: selected.farmerName },
              { label: 'Name (English)', value: selected.nameEnglish   || '—' },
              { label: 'Phone Number',   value: selected.phoneNumber   || '—' },
              { label: 'Mobile (SMS)',   value: selected.mobileNumber  || '—' },
              { label: 'Milk Type',      value: selected.milkTypeName  || getMilkTypeName(selected.milkTypeId) },
              { label: 'Bank Branch',    value: selected.bankBranch    || '—' },
              { label: 'Bank Name',      value: selected.bankName      || '—' },
              { label: 'Account Number', value: selected.accountNumber || '—' },
              { label: 'IFSC Code',      value: selected.ifscCode      || '—' },
            ].map(row => (
              <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.9, borderBottom: '1px solid #f5f5f5' }}>
                <Typography variant="body2" sx={{ color: '#888', fontWeight: 600 }}>{row.label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#222' }}>{row.value}</Typography>
              </Box>
            ))}
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => { setViewOpen(false); handleEdit(selected); }} variant="contained" startIcon={<Edit />} sx={btnSx}>Edit</Button>
          <Button onClick={() => { setViewOpen(false); handleStatusConfirm(selected); }} variant="outlined"
            sx={{ borderRadius: 2, color: isActive(selected) ? '#f97316' : theme.primary, borderColor: isActive(selected) ? '#f97316' : theme.primary }}>
            {isActive(selected) ? 'Deactivate' : 'Activate'}
          </Button>
          <Button onClick={() => setViewOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── STATUS CONFIRM DIALOG ── */}
      <Dialog open={statusOpen} onClose={() => setStatusOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{isActive(selected) ? 'Deactivate Farmer' : 'Activate Farmer'}</DialogTitle>
        <DialogContent>
          <Alert severity={isActive(selected) ? 'warning' : 'info'} sx={{ borderRadius: 2 }}>
            {isActive(selected)
              ? <>Are you sure you want to <strong>deactivate</strong> <strong>{selected?.farmerName}</strong>?</>
              : <>Are you sure you want to <strong>activate</strong> <strong>{selected?.farmerName}</strong>?</>}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setStatusOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleToggleStatus} variant="contained"
            sx={{ background: isActive(selected) ? '#f97316' : theme.primary, borderRadius: 2, fontWeight: 700, '&:hover': { background: isActive(selected) ? '#ea6c00' : theme.dark } }}>
            {isActive(selected) ? 'Yes, Deactivate' : 'Yes, Activate'}
          </Button>
        </DialogActions>
      </Dialog>

    </Layout>
  );
}